import { ActionContext } from 'vuex';
import { AxiosError } from 'axios';
import { PaymentOptionsStoreState } from '@/interfaces/payment-options';
import { getBankAccounts, getMoovToken, removeBankAccount, removeCard, syncCards } from '@/api/payment-options';
import { getOnboardingDetails } from '@/api/account';
import { OnboardingDetails } from '@/interfaces/onboarding';
import { MoovTokenResponse } from '@/interfaces/moov';

type Context = ActionContext<PaymentOptionsStoreState, never>;

/**
 * Creates a new moov account
 * @param context
 */
export async function fetchOnboardingDetails(context: Context): Promise<OnboardingDetails> {
  const onboardingDetails = context.getters.getOnboardingDetails;
  if (onboardingDetails) {
    return onboardingDetails;
  }

  const { data } = await getOnboardingDetails();
  context.commit('setOnboardingDetails', data);
  return data;
}

export async function getCardData(context: Context): Promise<void> {
  const { data } = await syncCards();
  context.commit('setCards', data);
}

export async function getPaymentOptions(context: Context): Promise<void> {
  context.commit('setLoading', true);

  try {
    const banksResponse = await getBankAccounts(context.getters.achProvider);
    context.commit('setBankAccounts', banksResponse.data);
  } catch (e) {
    const error = e as AxiosError;
    if (error.response?.status === 400) {
      context.commit('setBankAccounts', {});
    } else {
      throw e;
    }
  }

  const sendCardRequest = async () => {
    const cards = await syncCards();
    context.commit('setCards', cards.data);
  };

  try {
    // If this fails it means that user does not have a Moov account yet
    await sendCardRequest();
  } catch (e) {
    const error = e as AxiosError;
    if (error.response?.status === 400) {
      try {
        await context.dispatch('fetchOnboardingDetails');
        sendCardRequest();
      } catch {
        throw new Error();
      }
    } else {
      throw new Error();
    }
  } finally {
    context.commit('setLoading', false);
  }
}

export async function removePaymentMethod(context: Context, paymentMethod: { id: string, type: 'bank' | 'card' }) {
  if (paymentMethod.type === 'bank') {
    await removeBankAccount(paymentMethod.id);
  } else {
    await removeCard(paymentMethod.id);
  }

  context.dispatch('User/fetchUserInfo', null, { root: true });
}

export async function fetchMoovDropToken(context: Context): Promise<MoovTokenResponse> {
  const { data } = await getMoovToken();

  // if token is null, the user does not have a moov account
  if (data.oauth_token) {
    context.commit('setOnboardingDetails', data);
    return data;
  }

  // moov account does not exist, try to create
  const onboardingData = await fetchOnboardingDetails(context);

  return onboardingData;
}
